import { useCallback, useRef } from "react";
import type {
  EnterHandler,
  ExitHandler,
} from "react-transition-group/Transition";


import type { CssTransitionProps } from "./transition.css";
import { CSSRefTransition } from "./transition.css";
import { clamp } from "../utils/clamp";
import { findNearestScroller } from "../utils/dom.find-nearest-scroller";


type Props = CssTransitionProps & {
  /**
   * specifies which property to use when resolving the height and width of the
   * entering element.
   *
   * @default "scrollSize"
   */
  strategy?: "clientSize" | "scrollSize";

  /**
   * in case the size transition changes the scroll offset of the scrol
   * container and if the element transitioning needs to remain at the same
   * position within the viewport, then this prop may be toggled on to adjust
   * scroll offset on each render frame while the animation is running.
   */
  retainElementPositionDuringAnimation?: boolean | "window";

  /**
   * specifies the axis which the <SizeTransition /> will be able to animate.
   *
   * @example axis="horisontal" --> an element which will transition using the `width` property
   *
   * @example axis="vertical" --> an element which will transition using the `height` property
   *
   * @example axis="both" --> an elemenet which will transition using both the `width` and `height` properties.
   */
  axis: "horisontal" | "vertical" | "both";

  /**
   * If `swappable` is set to true the transition behaves differently.
   *
   * Instead of allowing the wrapped element to transition its `width` and
   * `height` properties, the parent will instead be able to transition its
   * size from an exiting <SizeTransition /> to an entering <SizeTransition />
   * based on the given axis.
   */
  swappable?: boolean;

  /**
   * If provided the transition will ensure that the applied sizes never
   * becomes smaller than the values provided.
   *
   * Do note that the values `min-width` and `min-height` are used to
   * implicitly determine this as well if they have been provided.
   */
  getMinSizeInPx?(): { width?: number; height?: number };

  /**
   * If provided the transition will ensure that the applied sizes never
   * becomes larger than the values provided.
   *
   * Do note that the values `max-width` and `max-height` are used to
   * implicitly determine this as well if they have been provided.
   */
  getMaxSizeInPx?(): { width?: number; height?: number };

  /**
   * In case transition properties are purposly not set at times, then
   * skipValidation can be set in order to supress validation warnings.
   */
  skipValidation?: boolean;
};

/**
 * acts similarily to the base <Transition /> component (which this component
 * actually builds upon), with the addition that `width` and `height`
 * properties can be transitioned, which is not natively supported by browsers.
 *
 * by default components applying this transition will grow from 0 to their
 * original size in the specified axis when entering and the opposite when
 * exiting.
 * If `swappable` is set to true the transition behaves differently. Instead,
 * the parent will be able to transition its size from an exiting
 * <SizeTransition /> to an entering <SizeTransition /> based on the given
 * axis.
 *
 * NB: The `transition` property MUST be declared on the HTMLElement that is
 * wrapped with the <SizeTransition /> with a value including the specified
 * axis.
 *
 * @example
 * ```
 * const contentCls = css`
 *  &-enter-active {
 *    transition: height 200ms linear;
 *  }
 *
 *  &-exit-active {
 *    transition: height 200ms linear;
 *  }
 * `
 *
 * <SizeTransition
 *  in={active}
 *  axis="horisontal"
 *  timeout={200}
 *  classNames={contentCls}
 * >
 *  {(ref) => <div ref={ref} className={contentCls} />}
 * </SizeTransition>
 * ```
 */
export function SizeTransition({
  axis,
  swappable,
  skipValidation,
  retainElementPositionDuringAnimation,
  children,
  getMinSizeInPx,
  getMaxSizeInPx,
  strategy,
  ...props
}: Props) {
  const lastEventRef = useRef<
    | "enter"
    | "enter-active"
    | "enter-done"
    | "exit"
    | "exit-active"
    | "exit-done"
    | undefined
  >(undefined);

  const internalRef = useRef<HTMLElement>(null);
  const nodeRef = props.nodeRef ?? internalRef;

  const elementPositionAnimationFrameRef = useRef(0);

  const onEnter: EnterHandler<HTMLElement> = useCallback(() => {
    lastEventRef.current = "enter";
    const element = nodeRef.current;
    const parent = element?.parentElement;

    if (!element) {
      return;
    }

    const constraints = getComputedConstraints(
      { getMaxSizeInPx, getMinSizeInPx, swappable },
      element
    );

    // in case swappable is provided, then the logic that applies the size
    // should be applied on the parent element
    if (swappable) {
      if (!parent) {
        console.warn("<SizeTransition />: Unable to resolve parent element.");
        return;
      }

      const siblings = getSiblings(element, parent);

      const constrainedHeight = clamp(
        constraints.minSizeInPx.height,
        siblings.map((it) => it.clientHeight),
        constraints.maxSizeInPx.height
      );

      const constrainedWidth = clamp(
        constraints.minSizeInPx.width,
        siblings.map((it) => it.clientWidth),
        constraints.maxSizeInPx.width
      );

      if (["both", "horisontal"].includes(axis) && !parent.style.width) {
        element.style.setProperty(
          "--size-transition--initial-width",
          `${constrainedWidth}px`
        );
        parent.style.width = `${constrainedWidth}px`;
      }

      if (["both", "vertical"].includes(axis) && !parent.style.height) {
        element.style.setProperty(
          "--size-transition--initial-height",
          `${constrainedHeight}px`
        );
        parent.style.height = `${constrainedHeight}px`;
      }
    } else {
      // ... else apply the size on the element itself as default
      if (["both", "horisontal"].includes(axis)) {
        element.style.setProperty(
          "--size-transition--initial-width",
          `${constraints.minSizeInPx.width}px`
        );
        element.style.width = `${constraints.minSizeInPx.width}px`;
      }

      if (["both", "vertical"].includes(axis)) {
        element.style.setProperty(
          "--size-transition--initial-height",
          `${constraints.minSizeInPx.height}px`
        );
        element.style.height = `${constraints.minSizeInPx.height}px`;
      }
    }

    if (retainElementPositionDuringAnimation) {
      const initialPosition = element.getBoundingClientRect().top;
      const scroller = findNearestScroller(element);

      const fixateElementPosition = () => {
        elementPositionAnimationFrameRef.current = window.requestAnimationFrame(
          fixateElementPosition
        );

        const currentPosition = element?.getBoundingClientRect().top;

        if (!currentPosition) {
          return;
        }

        const diff = currentPosition - initialPosition;

        if (scroller && retainElementPositionDuringAnimation !== "window") {
          scroller.scrollTop += diff;
        } else {
          window.scrollTo({
            left: window.scrollX,
            top: window.scrollY + diff,
            behavior: "auto",
          });
        }
      };

      elementPositionAnimationFrameRef.current = window.requestAnimationFrame(
        fixateElementPosition
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, axis, getMinSizeInPx, getMaxSizeInPx]);

  const onEntering: EnterHandler<HTMLElement> = useCallback(() => {
    lastEventRef.current = "enter-active";
    const element = nodeRef.current;
    const parent = element?.parentElement;

    if (!element) {
      return;
    }

    // slightly defer running the enter animation, which provides enough time
    // for certain components to properly mount and apply size calculations (e.g
    // a component, which can possibly apply height limitations within a layout
    // effect)
    if (lastEventRef.current !== "enter-active") {
      return;
    }

    const constraints = getComputedConstraints(
      { getMaxSizeInPx, getMinSizeInPx },
      element
    );

    const constrainedHeight = clamp(
      constraints.minSizeInPx.height,
      strategy === "clientSize" ? element.clientHeight : element.scrollHeight,
      constraints.maxSizeInPx.height
    );

    const constrainedWidth = clamp(
      constraints.minSizeInPx.width,
      strategy === "clientSize" ? element.clientWidth : element.scrollWidth,
      constraints.maxSizeInPx.width
    );

    // in case swappable is provided, then the logic that applies the size
    // should be applied on the parent element
    if (swappable) {
      if (!parent) {
        console.warn("<SizeTransition />: Unable to resolve parent element.");
        return;
      }

      validateElement(parent, { axis, swappable, skipValidation });

      if (["both", "horisontal"].includes(axis)) {
        parent.style.width = `${constrainedWidth}px`;
      }

      if (["both", "vertical"].includes(axis)) {
        parent.style.height = `${constrainedHeight}px`;
      }
    } else {
      // ... else apply the size on the element itself as default
      validateElement(element, { axis, swappable, skipValidation });

      if (["both", "horisontal"].includes(axis)) {
        element.style.width = `${constrainedWidth}px`;
      }

      if (["both", "vertical"].includes(axis)) {
        element.style.height = `${constrainedHeight}px`;
      }
    }
  }, [
    nodeRef,
    getMaxSizeInPx,
    getMinSizeInPx,
    strategy,
    swappable,
    axis,
    skipValidation,
  ]);

  const onEntered: EnterHandler<HTMLElement> = useCallback(() => {
    window.cancelAnimationFrame(elementPositionAnimationFrameRef.current);
    lastEventRef.current = "enter-done";
    const element = nodeRef.current;
    const parent = element?.parentElement;

    if (!element || !parent) {
      return;
    }

    const target = swappable ? parent : element;

    if (["both", "horisontal"].includes(axis)) {
      element.style.removeProperty("--size-transition--initial-width");
      target.style.width = "";
    }

    if (["both", "vertical"].includes(axis)) {
      element.style.removeProperty("--size-transition--initial-height");
      target.style.height = "";
    }
  }, [nodeRef, swappable, axis]);

  const onExit: ExitHandler<HTMLElement> = useCallback(() => {
    lastEventRef.current = "exit";
    const element = nodeRef.current;

    // If swappable is set to true then the application of size properties will
    // be handled by the entering element and thus we can safely bail out.
    if (!element || swappable) {
      return;
    }

    if (["both", "horisontal"].includes(axis)) {
      element.style.setProperty(
        "--size-transition--initial-width",
        `${element.clientWidth}px`
      );
      element.style.width = `${element.clientWidth}px`;
    }

    if (["both", "vertical"].includes(axis)) {
      element.style.setProperty(
        "--size-transition--initial-height",
        `${element.clientHeight}px`
      );
      element.style.height = `${element.clientHeight}px`;
    }
  }, [nodeRef, swappable, axis]);

  const onExiting: ExitHandler<HTMLElement> = useCallback(() => {
    lastEventRef.current = "exit-active";
    const element = nodeRef.current;

    // If swappable is set to true then the application of size properties will
    // be handled by the entering element and thus we can safely bail out.
    if (!element || swappable) {
      return;
    }

    // slightly defer running the exit animation, which provides enough time
    // for certain components to properly mount and apply size calculations (e.g
    // a component, which can possibly apply height limitations within a layout
    // effect)
    if (lastEventRef.current !== "exit-active") {
      return;
    }

    const constraints = getComputedConstraints(
      { getMaxSizeInPx, getMinSizeInPx },
      element
    );

    validateElement(element, { axis, swappable, skipValidation });

    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constraints.minSizeInPx.width}px`;
    }

    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constraints.minSizeInPx.height}px`;
    }
  }, [
    nodeRef,
    swappable,
    getMaxSizeInPx,
    getMinSizeInPx,
    axis,
    skipValidation,
  ]);

  const onExited: ExitHandler<HTMLElement> = useCallback(() => {
    lastEventRef.current = "exit-done";

    // @ts-expect-error <TransitionGroup /> will provide an onExited callback
    // which we need to manually execute once the transition has been fully
    // completed.
    // This callback is however NOT typed in our props as we do not want
    // integrations to provide their own onExited callback.
    props.onExited?.();
    // @ts-expect-error See the description above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onExited]);

  return (
    <CSSRefTransition
      {...props}
      nodeRef={nodeRef}
      onEnter={onEnter}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExiting={onExiting}
      onExited={onExited}
    >
      {children}
    </CSSRefTransition>
  );
}

/**
 * In case size constraints has been applied to the element which can be
 * calculated in pixels (or directly provided to this transition) then ensure
 * we limit the transition to these constraints.
 */
function getComputedConstraints(
  args: Pick<Props, "getMaxSizeInPx" | "getMinSizeInPx" | "swappable">,
  element: HTMLElement
): {
  minSizeInPx: { width: number; height: number };
  maxSizeInPx: { width: number; height: number };
} {
  const computedStyles = window.getComputedStyle(element);

  const minHeight =
    computedStyles.minHeight === "none" ||
      !computedStyles.minHeight.includes("px")
      ? 0
      : parseFloat(computedStyles.minHeight);

  const maxHeight =
    computedStyles.maxHeight === "none" ||
      !computedStyles.maxHeight.includes("px")
      ? Infinity
      : parseFloat(computedStyles.maxHeight);

  const minWidth =
    computedStyles.minWidth === "none" ||
      !computedStyles.minWidth.includes("px")
      ? 0
      : parseFloat(computedStyles.minWidth);

  const maxWidth =
    computedStyles.maxWidth === "none" ||
      !computedStyles.maxWidth.includes("px")
      ? Infinity
      : parseFloat(computedStyles.maxWidth);

  const explicitMinSize = args.getMinSizeInPx?.();
  const explicitMaxSize = args.getMaxSizeInPx?.();

  return {
    minSizeInPx: {
      width: args.swappable
        ? explicitMinSize?.width ?? 0
        : Math.max(minWidth, explicitMinSize?.width ?? 0),
      height: args.swappable
        ? explicitMinSize?.height ?? 0
        : Math.max(minHeight, explicitMinSize?.height ?? 0),
    },
    maxSizeInPx: {
      width: args.swappable
        ? explicitMaxSize?.width ?? Infinity
        : Math.min(maxWidth, explicitMaxSize?.width ?? Infinity),
      height: args.swappable
        ? explicitMaxSize?.height ?? Infinity
        : Math.min(maxHeight, explicitMaxSize?.height ?? Infinity),
    },
  };
}

function getSiblings(element: Element, parent: Element): Element[] {
  const siblings: Element[] = [];

  for (const child of Array.from(parent.children)) {
    if (child !== element) {
      siblings.push(child);
    }
  }

  return siblings;
}

/**
 * observes the provided element and ensures that it has all the CSS properties
 * required to properly integrate with the <SizeTransition />.
 */
function validateElement(
  element: HTMLElement,
  args: Pick<Props, "axis" | "swappable" | "skipValidation">
) {
  if (process.env["NODE_ENV"] === "production" || args.skipValidation) {
    return;
  }

  const computedStyles = getComputedStyle(element);

  const hasValidHeightTransition =
    computedStyles.transition !== "all 0s ease 0s" &&
    (computedStyles.transition.includes("all") ||
      computedStyles.transition.includes("height"));

  const hasValidWidthTransition =
    computedStyles.transition !== "all 0s ease 0s" &&
    (computedStyles.transform.includes("all") ||
      computedStyles.transition.includes("width"));

  const elementLabel = args.swappable
    ? "parent of the transition element"
    : "transitioning element";

  if (!hasValidHeightTransition && ["both", "vertical"].includes(args.axis)) {
    console.warn(
      `<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'height' property`,
      element
    );
  }

  if (!hasValidWidthTransition && ["both", "horisontal"].includes(args.axis)) {
    console.warn(
      `<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'width' property`,
      element
    );
  }
}
